import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  BaseWebsocketMessage,
  SubWebsocketMessageRequest,
} from '@qtek/core/websockets-core';
import {
  ErrorSnackBarData,
  NormalSnackBarData,
  SnackBarService,
} from '@qtek/libs/snack-bar-core';
import { TaskPayload } from '@qtek/shared/models';
import { isNil } from '@qtek/shared/utils';
import { EMPTY, pipe, Subject } from 'rxjs';
import { catchError, filter, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { TasksService } from './tasks.service';
import {
  applyInsTask,
  applyUpdTask,
  removeTaskFromList,
  TaskQueryParams,
  TaskRow,
  TasksByMysid,
} from './tasks.updaters';

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface TasksMeta {
  search?: any[];
  ops?: any;
  properties?: any[];
  sort?: any[];
  loading?: boolean;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface TasksState {
  allTasks: TasksByMysid;
  allTasksLoading: Record<string, boolean>;
  allTasksMeta: Record<string, TasksMeta>;
  queryParams: Record<string, TaskQueryParams>;
  queryViews: Record<string, string | undefined>;
  task: TaskRow | null;
  dealsForTask: { id: string; name: string }[];
}

export interface TasksConnectPayload {
  payload?: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>;
  mysid: string;
}

interface TaskWsMessage extends BaseWebsocketMessage {
  id?: string;
  // `res` is a row, a row list or absent depending on op.
  res?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  sts?: { message?: string; meta?: TasksMeta };
}

const initialState: TasksState = {
  allTasks: {},
  allTasksLoading: {},
  allTasksMeta: {},
  queryParams: {},
  queryViews: {},
  task: null,
  dealsForTask: [],
};

export const TasksStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    taskCreated$: new Subject<{ task: TaskRow; mysid: string }>(),
    taskCreateFailed$: new Subject<{ mysid: string }>(),
  })),
  withMethods(
    (
      store,
      tasksService = inject(TasksService),
      snackBar = inject(SnackBarService)
    ) => {
      const disconnect$ = new Subject<string>();
      const disconnectSingle$ = new Subject<void>();

      const ok = (message: string) =>
        snackBar.showSnackBar({
          type: 'NormalMessage',
          message,
        } as NormalSnackBarData);
      const fail = (message: string) =>
        snackBar.showSnackBar({
          type: 'ErrorMessage',
          message,
        } as ErrorSnackBarData);

      const setListLoading = (mysid: string, loading: boolean) =>
        patchState(store, state => ({
          allTasksLoading: { ...state.allTasksLoading, [mysid]: loading },
        }));

      // The ins/upd echo has no dealName, assignee or item info; replay the list query for the enriched rows.
      const refresh = (mysid: string) => {
        const prms = store.queryParams()[mysid];
        if (!prms) return;
        tasksService.queryTasks({
          mysid,
          view: store.queryViews()[mysid],
          prms,
        });
      };

      const onListMessage = (mysid: string, m: TaskWsMessage) => {
        const { op, res, sts, id } = m;
        switch (op) {
          case 'query':
            if (!res && isNil(sts?.meta)) {
              setListLoading(mysid, false);
              return;
            }
            patchState(store, state => ({
              allTasks: { ...state.allTasks, [mysid]: res ?? [] },
              allTasksLoading: { ...state.allTasksLoading, [mysid]: false },
              ...(sts?.meta
                ? {
                    allTasksMeta: { ...state.allTasksMeta, [mysid]: sts.meta },
                  }
                : {}),
            }));
            return;
          case 'ins':
            if (!res) {
              fail('GNR_TASK_CREATE_FAILED');
              setListLoading(mysid, false);
              store.taskCreateFailed$.next({ mysid });
              return;
            }
            patchState(store, state => ({
              allTasks: applyInsTask(
                state.allTasks,
                state.queryParams,
                res,
                mysid
              ),
              allTasksLoading: { ...state.allTasksLoading, [mysid]: false },
            }));
            refresh(mysid);
            store.taskCreated$.next({ task: res, mysid });
            return;
          case 'upd':
          case 'upds':
            if (!res) {
              fail('GNR_TASK_UPDATE_FAILED');
              return;
            }
            patchState(store, state => ({
              allTasks: applyUpdTask(state.allTasks, state.queryParams, res),
            }));
            refresh(mysid);
            return;
          case 'del':
            if (!res) {
              fail('GNR_TASK_DELETE_FAILED');
              return;
            }
            // v2 carries the deleted task's id on the envelope, not in `res`.
            patchState(store, state => ({
              allTasks: removeTaskFromList(
                state.allTasks,
                mysid,
                res._id ?? id ?? ''
              ),
            }));
            return;
          default:
            return;
        }
      };

      return {
        tasksByMysid(mysid: string) {
          return computed(() => store.allTasks()[mysid] ?? []);
        },

        loadingByMysid(mysid: string) {
          return computed(() => store.allTasksLoading()[mysid] ?? false);
        },

        metaByMysid(mysid: string) {
          return computed(() => store.allTasksMeta()[mysid] ?? null);
        },

        connect: rxMethod<TasksConnectPayload>(
          pipe(
            tap(({ payload, mysid }) =>
              patchState(store, state => ({
                allTasksLoading: { ...state.allTasksLoading, [mysid]: true },
                queryParams: {
                  ...state.queryParams,
                  [mysid]: payload?.prms ?? {},
                },
                queryViews: { ...state.queryViews, [mysid]: payload?.view },
              }))
            ),
            mergeMap(({ payload, mysid }) =>
              tasksService
                .subscribe(
                  payload as Omit<SubWebsocketMessageRequest, 'ent' | 'op'>,
                  mysid
                )
                .pipe(
                  takeUntil(disconnect$.pipe(filter(id => id === mysid))),
                  filter(m => !!m.op),
                  tap(m => onListMessage(mysid, m as TaskWsMessage)),
                  catchError(() => {
                    setListLoading(mysid, false);
                    return EMPTY;
                  })
                )
            )
          )
        ),

        disconnect(mysid: string): void {
          disconnect$.next(mysid);
          patchState(store, state => {
            const { [mysid]: _t, ...allTasks } = state.allTasks;
            const { [mysid]: _m, ...allTasksMeta } = state.allTasksMeta;
            const { [mysid]: _l, ...allTasksLoading } = state.allTasksLoading;
            const { [mysid]: _p, ...queryParams } = state.queryParams;
            const { [mysid]: _v, ...queryViews } = state.queryViews;
            return {
              allTasks,
              allTasksMeta,
              allTasksLoading,
              queryParams,
              queryViews,
            };
          });
        },

        connectSingle: rxMethod<string>(
          pipe(
            mergeMap(taskId =>
              tasksService.subscribeSingleTask(taskId).pipe(
                takeUntil(disconnectSingle$),
                filter(m => m.op === 'get' && !!(m as TaskWsMessage).res),
                tap(m =>
                  patchState(store, { task: (m as TaskWsMessage).res ?? null })
                ),
                catchError(() => EMPTY)
              )
            )
          )
        ),

        disconnectSingle(): void {
          disconnectSingle$.next();
          patchState(store, { task: null });
        },

        createTask(payload: TaskPayload, mysid: string): void {
          tasksService.createTask(payload, mysid);
          ok('GNR_TASK_CREATED_SUCCESSFULLY');
        },

        updateTask(payload: TaskPayload, mysid: string, id: string): void {
          tasksService.updateTask(payload, mysid, id);
          ok('GNR_TASK_UPDATED_SUCCESSFULLY');
        },

        deleteTasks(taskIds: string[], mysid: string): void {
          tasksService.deleteTasks(taskIds, mysid);
          ok('GNR_TASK_DELETED_SUCCESSFULLY');
        },

        loadDealsForTask: rxMethod<void>(
          pipe(
            mergeMap(() =>
              tasksService.getDealsForTask().pipe(
                tap(({ res }) => {
                  if (res) patchState(store, { dealsForTask: res });
                }),
                catchError(() => EMPTY)
              )
            )
          )
        ),
      };
    }
  )
);
